import { useState } from "react";
import AnnouncementBar from "@/components/aeonyx/AnnouncementBar";
import Header from "@/components/aeonyx/Header";
import Footer from "@/components/aeonyx/Footer";
import { Phone, EnvelopeSimple, MapPin, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ContactAPI, NewsletterAPI, OrdersAPI } from "@/lib/supabaseServices";

export default function ContactPage() {
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [newsEmail, setNewsEmail] = useState("");
  const [newsLoading, setNewsLoading] = useState(false);
  const [orderEmail, setOrderEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.email || !contact.email.includes("@") || !contact.message) {
      toast.error("Please provide a valid email and a message");
      return;
    }
    setContactLoading(true);
    const { error } = await ContactAPI.submit(contact);
    setContactLoading(false);
    if (error) return toast.error(error.message || "Could not send message");
    toast.success("Message sent — we'll be in touch");
    setContact({ name: "", email: "", subject: "", message: "" });
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!newsEmail.includes("@")) return toast.error("Please enter a valid email");
    setNewsLoading(true);
    const { error } = await NewsletterAPI.subscribe(newsEmail);
    setNewsLoading(false);
    if (error) return toast.error(error.message || "Subscribe failed");
    toast.success("Subscribed. Welcome aboard.");
    setNewsEmail("");
  };

  const fetchOrders = async (e) => {
    e.preventDefault();
    if (!orderEmail.includes("@")) return toast.error("Please enter a valid email");
    setOrdersLoading(true);
    const { data, error } = await OrdersAPI.byEmail(orderEmail);
    setOrdersLoading(false);
    if (error) return toast.error(error.message || "Could not fetch orders");
    setOrders(data);
    if (!data.length) toast.info("No orders found for that email");
  };

  const Field = ({ id, label, ...props }) => (
    <div>
      <Label htmlFor={id} className="text-xs text-navy-deep">{label}</Label>
      <Input id={id} {...props} className="mt-1.5 h-11 rounded-xl" />
    </div>
  );

  return (
    <div className="bg-cool-white min-h-screen">
      <AnnouncementBar />
      <Header />
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="overline text-cyan-brand">Contact</div>
          <h1 className="font-display mt-4 text-4xl md:text-5xl font-bold text-navy-deep">
            Get in touch with Aeonyx.
          </h1>

          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {[
              { icon: Phone, label: "Phone", value: "[PHONE]" },
              { icon: EnvelopeSimple, label: "Email", value: "[EMAIL]" },
              { icon: MapPin, label: "Address", value: "[ADDRESS]" },
            ].map((c) => (
              <div key={c.label} className="rounded-3xl bg-white border border-[#8A97A8]/25 p-6">
                <div className="h-11 w-11 rounded-2xl bg-signature-gradient text-white grid place-items-center">
                  <c.icon size={18} weight="regular" />
                </div>
                <div className="overline text-cool-grey mt-4 text-[10px]">{c.label}</div>
                <div className="font-mono-data text-navy-deep mt-1 text-[14px]">{c.value}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {/* Contact form */}
            <form onSubmit={submitContact} className="rounded-3xl bg-white border border-[#8A97A8]/25 p-8 space-y-4">
              <div className="overline text-cyan-brand">Send us a message</div>
              <Field id="c-name"    label="Name"    value={contact.name}    onChange={(e)=>setContact({...contact, name: e.target.value})} placeholder="Jane Doe" />
              <Field id="c-email"   label="Email"   type="email" required value={contact.email}   onChange={(e)=>setContact({...contact, email: e.target.value})} placeholder="you@lab.com" />
              <Field id="c-subject" label="Subject" value={contact.subject} onChange={(e)=>setContact({...contact, subject: e.target.value})} />
              <div>
                <Label htmlFor="c-msg" className="text-xs text-navy-deep">Message</Label>
                <Textarea id="c-msg" required rows={5} value={contact.message} onChange={(e)=>setContact({...contact, message: e.target.value})} className="mt-1.5 rounded-xl" />
              </div>
              <Button type="submit" disabled={contactLoading} className="w-full h-11 rounded-full bg-cta-gradient text-white font-semibold">
                {contactLoading ? <CircleNotch className="animate-spin" size={16} /> : "Send message"}
              </Button>
            </form>

            {/* Newsletter + order lookup */}
            <div className="space-y-6">
              <form onSubmit={submitNewsletter} className="rounded-3xl bg-white border border-[#8A97A8]/25 p-8 space-y-4">
                <div className="overline text-cyan-brand">Newsletter</div>
                <p className="text-sm text-slate-600">Get research updates and product news.</p>
                <div className="flex gap-2">
                  <Input type="email" placeholder="you@lab.com" value={newsEmail} onChange={(e)=>setNewsEmail(e.target.value)} className="h-11 rounded-xl flex-1" required />
                  <Button type="submit" disabled={newsLoading} className="h-11 px-5 rounded-full bg-navy-deep text-white">
                    {newsLoading ? <CircleNotch className="animate-spin" size={16} /> : "Subscribe"}
                  </Button>
                </div>
              </form>

              <form onSubmit={fetchOrders} className="rounded-3xl bg-white border border-[#8A97A8]/25 p-8 space-y-4">
                <div className="overline text-cyan-brand">Order lookup</div>
                <p className="text-sm text-slate-600">Enter the email you used at checkout.</p>
                <div className="flex gap-2">
                  <Input type="email" placeholder="you@lab.com" value={orderEmail} onChange={(e)=>setOrderEmail(e.target.value)} className="h-11 rounded-xl flex-1" required />
                  <Button type="submit" disabled={ordersLoading} className="h-11 px-5 rounded-full bg-cta-gradient text-white">
                    {ordersLoading ? <CircleNotch className="animate-spin" size={16} /> : "Look up"}
                  </Button>
                </div>
                {orders && orders.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {orders.map((o) => (
                      <li key={o.id} className="rounded-xl border border-[#8A97A8]/20 p-3 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium text-navy-deep">#{o.order_number || o.id.slice(0, 8)}</span>
                          <span className="font-mono-data text-cool-grey">${o.total}</span>
                        </div>
                        <div className="text-xs text-cool-grey mt-1">
                          {o.payment_status} · {o.order_status}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
